/**
 * DeleteAccountConfirm — the explicit irreversibility confirmation for
 * member account erasure (033 US2, T029; FR-006).
 *
 * The erasure model is PROMPT: no grace window, no pending-deletion
 * state, no post-acceptance cancellation. That makes THIS dialog the
 * one place the member can turn back, so the finality copy
 * (`myData.delete.irreversible`) and the cancel affordance render
 * BEFORE the destructive commit — the confirmation step IS the
 * contract, not chrome.
 *
 * States (single `phase` discriminant, no orthogonal booleans):
 *   confirm  — finality copy + retained-records note, Peruuta / Poista
 *              tili pysyvästi (destructive)
 *   pending  — the destructive button disables (double-submit guard)
 *   done     — the FR-006 completion confirmation; `onCompleted` fires
 *              with `completedAt` (the call site signs the member out /
 *              navigates); no destructive affordance remains
 *   unavailable — the API's P7 refusal (`ErasureUnavailableError`,
 *              retention windows unconfigured until feature 10): a calm
 *              "not available yet", terminal for this dialog
 *   error    — transient failure; the member can retry
 *
 * Controlled `open`/`onOpenChange` (the FeedbackModal pattern). While
 * pending, EVERY dismissal route is blocked: our buttons disable, and
 * Radix's Escape / overlay-click dismissals are intercepted too, so the
 * dialog stays mounted until the request settles. That keeps the
 * done/error phase visible and lets `onCompleted` fire with the
 * completion screen shown, rather than after an unmount (a conditionally
 * mounted call site would otherwise tear the dialog down mid-request and
 * strand the redirect with no visible outcome). The cascade is
 * transactional server-side, so a committed erasure is never partial.
 *
 * Token-backed classes only (no raw colours, no bracket classes).
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md.
 */

import { type JSX, useState } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { Dialog } from '../primitives/Dialog.js';
import { ErasureUnavailableError } from './AccountDeletionPort.js';
import { useDeleteAccount } from './useDeleteAccount.js';

export interface DeleteAccountConfirmProps {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  /**
   * Fires once, after the completion confirmation is shown. The call
   * site owns what follows (navigate, then sign out — the erased
   * member's session no longer exists server-side).
   */
  readonly onCompleted?: (completedAt: string) => void;
}

type Phase =
  | { readonly kind: 'confirm' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'done' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'error' };

const bodyTextClasses = 'text-sm text-foreground';
const noteTextClasses = 'text-sm text-muted-foreground';
const statusTextClasses = 'text-sm text-foreground text-center py-2';
const actionRowClasses = 'flex justify-end gap-3 pt-2';
const columnClasses = 'flex flex-col gap-3';

export function DeleteAccountConfirm({
  open,
  onOpenChange,
  onCompleted,
}: DeleteAccountConfirmProps): JSX.Element {
  const t = useTranslate();
  const deleteAccount = useDeleteAccount();
  const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });

  // Reset to a fresh confirm phase on every closed→open transition (the
  // React "adjust state during render" idiom, no Effect: R10/R11).
  // Without it, a member who hit error/unavailable, closed, and reopened
  // could land on the stale terminal screen with no route back to a fresh
  // confirmation. This reset is defensive: it keeps the confirm phase
  // correct whether a host mounts this once and toggles `open` (the
  // controlled-Root pattern, as DeleteAccountConfirm.test.tsx's
  // OpenToggler does) OR mounts it per-open and unmounts on close (the
  // current MyDataSection call site, where a fresh mount's useState
  // initialiser already yields a fresh confirm phase).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setPhase({ kind: 'confirm' });
    }
  }

  async function confirm(): Promise<void> {
    setPhase({ kind: 'pending' });
    try {
      const { completedAt } = await deleteAccount();
      setPhase({ kind: 'done' });
      // Fires once the erasure has COMMITTED (lead ruling, slice-B
      // review): the cascade is done and the member's auth_sessions are
      // hard-deleted server-side, so the call site's sign-out must
      // proceed: keeping the SPA "signed in" on a dead session would
      // fake continuity (P7). The pending-phase dismissal guard keeps the
      // dialog mounted through settle, so this fires with the completion
      // screen shown rather than after an unmount.
      onCompleted?.(completedAt);
    } catch (err) {
      setPhase(
        err instanceof ErasureUnavailableError ? { kind: 'unavailable' } : { kind: 'error' },
      );
    }
  }

  // While the erasure is in flight, ignore every dismissal route (Escape,
  // overlay pointer-down, Cancel): the dialog must stay mounted until the
  // request settles, so the done/error phase renders and, on a committed
  // erasure, onCompleted fires with the completion screen visible rather
  // than after an unmount. Radix routes every close trigger through
  // onOpenChange(false), so this handler is the load-bearing block; the
  // Content interceptors below are belt-and-suspenders that also stop the
  // close animation from ever starting.
  const handleOpenChange = (next: boolean): void => {
    if (!next && phase.kind === 'pending') return;
    onOpenChange(next);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        onEscapeKeyDown={(event) => {
          if (phase.kind === 'pending') event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (phase.kind === 'pending') event.preventDefault();
        }}
      >
        <div className={columnClasses}>
          <Dialog.Title>{t('myData.delete.title')}</Dialog.Title>
          {/* Radix wires the description into aria-describedby; the
              finality sentence doubles as the dialog's description so
              AT users hear the irreversibility right after the title. */}
          <Dialog.Description className={bodyTextClasses}>
            {t('myData.delete.irreversible')}
          </Dialog.Description>

          {(phase.kind === 'confirm' || phase.kind === 'pending' || phase.kind === 'error') && (
            <>
              <p className={noteTextClasses}>{t('myData.delete.retainedNote')}</p>
              {phase.kind === 'error' && (
                <p role="alert" className={statusTextClasses}>
                  {t('myData.delete.error')}
                </p>
              )}
              <div className={actionRowClasses}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={phase.kind === 'pending'}
                  onClick={() => onOpenChange(false)}
                >
                  {t('myData.delete.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={phase.kind === 'pending'}
                  onClick={() => {
                    void confirm();
                  }}
                >
                  {phase.kind === 'pending'
                    ? t('myData.delete.pending')
                    : t('myData.delete.confirm')}
                </Button>
              </div>
            </>
          )}

          {phase.kind === 'done' && (
            <>
              <p role="status" className={statusTextClasses}>
                {t('myData.delete.done')}
              </p>
              <div className={actionRowClasses}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  {t('myData.delete.doneClose')}
                </Button>
              </div>
            </>
          )}

          {phase.kind === 'unavailable' && (
            <>
              <p role="status" className={statusTextClasses}>
                {t('myData.delete.unavailable')}
              </p>
              <div className={actionRowClasses}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  {t('myData.delete.doneClose')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
